import { supabase } from "@/lib/supabase";
import { callEdgeFunction } from "@/lib/supabaseAuth";
import type { IQuestImageService } from "@/services/interfaces/IQuestImageService";
import {
  MAX_IMAGE_BYTES,
  nextImageOrder,
  sortImages,
  validateImageFile,
  type QuestImage,
} from "@/lib/questImages";

const BUCKET = "quest-images";

interface QuestImageRow {
  id: string;
  quest_id: string;
  storage_path: string;
  source: string;
  status: string;
  media_type: string | null;
  alt_text: string | null;
  order: number;
  created_at: string;
}

/** Resolve the object's public URL. The bucket is public by design — signed
 *  URLs would break anonymous discovery and any future share card, and these
 *  images are not private data. See 20260918120100 for that trade. */
function publicUrl(path: string): string | null {
  if (!path) return null;
  return supabase.storage.from(BUCKET).getPublicUrl(path).data.publicUrl ?? null;
}

function mapRow(row: QuestImageRow): QuestImage {
  return {
    id: row.id,
    questId: row.quest_id,
    storagePath: row.storage_path,
    source: row.source as QuestImage["source"],
    status: row.status as QuestImage["status"],
    mediaType: row.media_type,
    altText: row.alt_text,
    order: row.order,
    createdAt: row.created_at,
    url: publicUrl(row.storage_path),
  };
}

export class SupabaseQuestImageService implements IQuestImageService {
  /**
   * Lists from the TABLE, not from storage.
   *
   * A non-recursive storage listing returns pseudo-rows for subfolders, which
   * have already rendered as zero-byte files elsewhere in this app — and here
   * the reserved `generated/` prefix guarantees such a pseudo-row exists on
   * every quest the pipeline has touched. The table is also the only place
   * that knows source, status and alt text.
   */
  async list(questId: string): Promise<QuestImage[]> {
    const { data, error } = await supabase
      .from("quest_images")
      .select("id, quest_id, storage_path, source, status, media_type, alt_text, order, created_at")
      .eq("quest_id", questId);

    if (error) throw new Error(`Failed to load quest images: ${error.message}`);
    return sortImages((data as QuestImageRow[]).map(mapRow));
  }

  async listForQuests(questIds: string[]): Promise<Map<string, QuestImage[]>> {
    const out = new Map<string, QuestImage[]>();
    if (questIds.length === 0) return out;
    const { data, error } = await supabase
      .from("quest_images")
      .select("id, quest_id, storage_path, source, status, media_type, alt_text, order, created_at")
      .in("quest_id", questIds);

    if (error) throw new Error(`Failed to load quest images: ${error.message}`);
    for (const row of data as QuestImageRow[]) {
      const rows = out.get(row.quest_id) ?? [];
      rows.push(mapRow(row));
      out.set(row.quest_id, rows);
    }
    for (const [id, rows] of out) out.set(id, sortImages(rows));
    return out;
  }

  async upload(questId: string, file: File): Promise<QuestImage> {
    // Before any network call. The bucket enforces the same two limits, so
    // this is about failing in the picker rather than after 5 MB crossed the
    // wire — not about trusting the client.
    const check = validateImageFile(file);
    if (!check.ok) {
      throw new Error(
        check.reason === "too_large"
          ? `Image must be ${Math.floor(MAX_IMAGE_BYTES / 1024 / 1024)} MB or smaller`
          : check.reason === "empty"
            ? "That file is empty"
            : "Images must be WebP, PNG or JPEG",
      );
    }

    // Never under the reserved `generated/` segment — the object policies in
    // 20260918120100 refuse a creator write there, so a path that collided
    // with it would fail server-side anyway.
    const safeName = file.name.replace(/[\\/\u0000-\u001f\u007f]/g, "_") || "image";
    const path = `${questId}/${Date.now()}-${crypto.randomUUID()}-${safeName}`;

    const { error: uploadError } = await supabase.storage
      .from(BUCKET)
      .upload(path, file, { contentType: file.type });
    if (uploadError) throw new Error(`Failed to upload image: ${uploadError.message}`);

    // Appended after everything already in the gallery, illustrations
    // included, so a new photo never displaces the creator's chosen hero.
    const { data: existing, error: orderError } = await supabase
      .from("quest_images")
      .select("order")
      .eq("quest_id", questId);
    if (orderError) {
      await supabase.storage.from(BUCKET).remove([path]);
      throw new Error(`Failed to save image: ${orderError.message}`);
    }

    const { data, error } = await supabase
      .from("quest_images")
      .insert({
        quest_id: questId,
        storage_path: path,
        source: "upload",
        status: "ready",
        media_type: file.type,
        order: nextImageOrder((existing ?? []) as { order: number }[]),
      })
      .select("id, quest_id, storage_path, source, status, media_type, alt_text, order, created_at")
      .single();

    if (error) {
      // The row is the thing that makes the object reachable. Without this the
      // failed insert leaves a publicly fetchable orphan and, worse, silently
      // consumes nothing — the creator retries and accumulates orphans.
      await supabase.storage.from(BUCKET).remove([path]);
      throw new Error(`Failed to save image: ${error.message}`);
    }

    return mapRow(data as QuestImageRow);
  }

  async remove(image: Pick<QuestImage, "id" | "storagePath">): Promise<void> {
    // Row first: if the object delete fails, the image is already gone from
    // every gallery and the orphan is invisible rather than visible. The other
    // order can leave a row pointing at nothing, which renders as a broken
    // frame on the detail page.
    const { error } = await supabase.from("quest_images").delete().eq("id", image.id);
    if (error) throw new Error(`Failed to remove image: ${error.message}`);

    if (image.storagePath) {
      const { error: objectError } = await supabase.storage
        .from(BUCKET)
        .remove([image.storagePath]);
      if (objectError) {
        console.warn("[quest-images] row removed but object remains", image.storagePath, objectError);
      }
    }
  }

  /**
   * Full sequential re-index, never a swap of two rows.
   *
   * Partial re-indexing is what produces duplicate order values, and duplicates
   * are exactly what the gallery's timestamp tiebreak exists to paper over —
   * relying on that tiebreak for a deliberate ordering would make the creator's
   * arrangement depend on insert time.
   */
  async reorder(questId: string, orderedIds: string[]): Promise<void> {
    for (const [index, id] of orderedIds.entries()) {
      const { error } = await supabase
        .from("quest_images")
        .update({ order: index })
        .eq("id", id)
        .eq("quest_id", questId);
      if (error) throw new Error(`Failed to reorder images: ${error.message}`);
    }
  }

  async regenerate(questId: string): Promise<void> {
    // `force` is gated on the profile role inside the function. Sending it from
    // here grants nothing — a non-admin caller gets a 403 without anything
    // being deleted or billed.
    await callEdgeFunction("quest-images", { body: { questId, force: true } });
  }
}
